<template>
  <div class="w-full">
    <a-select
      v-model:value="selectedObjects"
      mode="multiple"
      class="w-full"
      :placeholder="placeholder"
      :loading="loading"
      show-search
      :filter-option="filterOption"
      :max-tag-count="'responsive'"
      @change="handleChange"
    >
      <template #suffixIcon>
        <ThemeImage
          :icon="imageNames.BOTTOM_ARROW"
          alt="dropdown"
          class="size-4"
        />
      </template>

      <a-select-opt-group
        v-for="group in groupedObjects"
        :key="`svc:${group.service}`"
        :label="group.label"
      >
        <a-select-option
          v-for="obj in group.objects"
          :key="`opt:${obj.id}`"
          :value="obj.id"
          :label="obj.name"
        >
          <div class="flex items-center justify-between w-full">
            <span class="text-sm text_color">{{ obj.name }}</span>
            <ThemeImage
              v-if="selectedObjects.includes(obj.id)"
              :icon="imageNames.TICK_GREY"
              alt="selected"
              class="size-4"
            />
          </div>
        </a-select-option>
      </a-select-opt-group>
    </a-select>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { standardObjectRouteNames } from "@/src/constants/objectConstants.js";
import { formatServiceLabel } from "@/src/constants/crmConstants.js";

const store = useStore();

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  excludeSlug: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Select objects...",
  },
  autoFetch: {
    type: Boolean,
    default: true,
  },
  includeStandardObjects: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "select"]);

// Use store getters instead of local refs
const loading = computed(() => store.getters["objects/loadingObjects"]);
const availableObjects = computed(() => store.getters["objects/objects"]);
const selectedObjects = ref([]);

// Exclude system objects and specific slugs that shouldn't be selectable for record type references
const excludedSlugs = [
  entityTypes.TASKS,
  entityTypes.TICKETS,
  "calls",        // Call - system object
  "crm_tasks",    // CRM Task - system object
  "forms",        // Form - system object
  "log_meetings", // Log Meeting - system object
];


watch(
  () => props.modelValue,
  (newValue) => {
    if (Array.isArray(newValue)) {
      selectedObjects.value = [...newValue];
    }
  },
  { immediate: true }
);

const filteredObjects = computed(() => {
  return availableObjects.value.filter((obj) =>
    !excludedSlugs.includes(obj.slug) &&
    obj.slug !== props.excludeSlug &&
    (!obj.is_system || (props.includeStandardObjects && standardObjectRouteNames.includes(obj.slug)))
  );
});

// Group options by service so same-name objects across services can be told
// apart at a glance (e.g. a CRM-owned "Product" vs the products-service one).
const SERVICE_ORDER = ["crm"];
const groupedObjects = computed(() => {
  const groups = new Map();
  for (const obj of filteredObjects.value) {
    const service = obj.service || "crm";
    if (!groups.has(service)) groups.set(service, []);
    groups.get(service).push(obj);
  }
  return Array.from(groups.entries())
    .map(([service, objects]) => ({
      service,
      label: formatServiceLabel(service),
      objects,
    }))
    .sort((a, b) => {
      const ai = SERVICE_ORDER.indexOf(a.service);
      const bi = SERVICE_ORDER.indexOf(b.service);
      if (ai !== -1 || bi !== -1) {
        return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
      }
      return a.label.localeCompare(b.label);
    });
});

const filterOption = (input, option) => {
  return (option.label || "").toLowerCase().includes(input.toLowerCase());
};

const handleChange = (values) => {
  emit("update:modelValue", values);
  emit("select", values.map((objectId) => {
    const obj = availableObjects.value.find((o) => o.id === objectId);
    return {
      object_id: objectId,
      object_type: obj?.slug || objectId,
      object_name: obj?.name || objectId,
    };
  }));
};

// Use store action instead of direct API call
const fetchObjects = async () => {
  await store.dispatch("objects/fetchObjects");
};

const reset = () => {
  selectedObjects.value = [];
};

onMounted(() => {
  if (props.autoFetch) {
    fetchObjects();
  }
});

defineExpose({
  fetchObjects,
  reset,
  availableObjects,
});
</script>

<style scoped>
:deep(.ant-select-selector) {
  min-height: 36px;
}

:deep(.ant-select-selection-item) {
  background-color: var(--secondary-button-bg);
  border-color: var(--border-color);
  border-radius: 4px;
}
</style>
